/**
 * PageEditor - 单页编辑卡片
 */
import { motion } from 'framer-motion';
import { FileText, Palette, UserCheck } from 'lucide-react';
import { EditorPage, EditorCharacter } from '../../../store/useDIYEditorStore';
import PromptHelper from './PromptHelper';

interface PageEditorProps {
  page: EditorPage;
  isCover?: boolean;
  coverTitle?: string;
  coverAuthor?: string;
  characters: EditorCharacter[];
  onUpdate: (data: Partial<EditorPage>) => void;
  onUpdateCover?: (data: { title?: string; author?: string }) => void;
}

function getCharacterEmoji(type: string, species?: string) {
  if (type === 'protagonist') return '👑';
  const emojiMap: Record<string, string> = {
    rabbit: '🐰', fox: '🦊', bird: '🐦', bear: '🐻', cat: '🐱', dog: '🐶', dinosaur: '🦖',
    whale: '🐋', bee: '🐝', penguin: '🐧', dolphin: '🐬', panda: '🐼',
    lion: '🦁', tiger: '🐯', monkey: '🐵', elephant: '🐘', frog: '🐸',
    butterfly: '🦋', turtle: '🐢', squirrel: '🐿️', koala: '🐨', giraffe: '🦒',
    owl: '🦉', hedgehog: '🦔', seal: '🦭',
  };
  return emojiMap[species || ''] || '🐾';
}

export default function PageEditor({
  page,
  isCover = false,
  coverTitle = '',
  coverAuthor = '',
  characters,
  onUpdate,
  onUpdateCover,
}: PageEditorProps) {
  return (
    <motion.div
      key={isCover ? 'cover' : page.pageNumber}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-5"
    >
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">
          {isCover ? '📘 封面' : `📄 第 ${page.pageNumber} 页`}
        </h2>
        <span className="text-xs text-slate-400">
          {isCover ? '封面的插图会作为绘本的展示封面' : '每页包含故事文字和对应的插图描述'}
        </span>
      </div>

      {/* 封面特有字段 */}
      {isCover && onUpdateCover && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <FileText className="w-4 h-4 text-indigo-500" />
              封面标题
            </label>
            <input
              type="text"
              value={coverTitle}
              onChange={(e) => onUpdateCover({ title: e.target.value })}
              placeholder="绘本封面标题（可不同于绘本总标题）"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-base"
            />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <UserCheck className="w-4 h-4 text-indigo-500" />
              作者署名
            </label>
            <input
              type="text"
              value={coverAuthor}
              onChange={(e) => onUpdateCover({ author: e.target.value })}
              placeholder="如：By 小明 & 妈妈"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-base"
            />
          </div>
        </>
      )}

      {/* 故事文字（非封面） */}
      {!isCover && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
            <FileText className="w-4 h-4 text-indigo-500" />
            故事文字 <span className="text-red-500">*</span>
            <span className="text-red-500 text-xs font-normal">文案就是生成绘本音频的文案</span>
          </label>
          <textarea
            value={page.content}
            onChange={(e) => onUpdate({ content: e.target.value })}
            placeholder={`写这一页的故事，比如：\n• 发生了什么？\n• 主角说了什么或做了什么？\n• 这一页要让小朋友感受到什么？\n（建议 1~3 句话，不要太长）`}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none text-base leading-relaxed"
            style={{ minHeight: '120px' }}
          />
        </div>
      )}

      {/* 画面描述 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
          <Palette className="w-4 h-4 text-indigo-500" />
          画面描述（告诉 AI 怎么画）<span className="text-red-500">*</span>
        </label>
        <textarea
          value={page.illustrationPrompt}
          onChange={(e) => onUpdate({ illustrationPrompt: e.target.value })}
          placeholder={`描述你想让 AI 画出的画面，尽量具体：\n• 场景在哪里？（森林、太空、家里...）\n• 角色在做什么动作？\n• 画面的氛围和颜色？\n• 有什么细节？`}
          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none text-base leading-relaxed"
          style={{ minHeight: '140px' }}
        />
        <div className="flex items-center justify-between mt-2">
          <PromptHelper />
          <span className="text-xs text-slate-400">
            {page.illustrationPrompt.length} 字
          </span>
        </div>
      </div>

      {/* 角色出场选择 */}
      {!isCover && characters.length > 0 && (
        <div className="bg-indigo-50/50 rounded-xl border border-indigo-100 p-4">
          <label className="flex items-center gap-2 text-sm font-semibold text-indigo-800 mb-2">
            <UserCheck className="w-4 h-4 text-indigo-500" />
            这页出场的角色
          </label>
          <p className="text-xs text-indigo-600/70 mb-2">
            勾选出场的角色，AI 会把它们画进这一页的插图里
          </p>
          <div className="flex flex-wrap gap-2">
            {characters.map((char) => {
              const isChecked = (page.charactersInPage || []).includes(char.name);
              return (
                <button
                  key={char.id}
                  onClick={() => {
                    const current = page.charactersInPage || [];
                    const next = isChecked
                      ? current.filter((n) => n !== char.name)
                      : [...current, char.name];
                    onUpdate({ charactersInPage: next });
                  }}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all
                    ${isChecked
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50'
                    }
                  `}
                >
                  <span>{isChecked ? '✓' : getCharacterEmoji(char.type, char.species)}</span>
                  <span>{char.name}</span>
                  {char.type === 'protagonist' && (
                    <span className="text-[10px] opacity-70">(主角)</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
}
